import { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Server } from '../api/types.js';
import { useAuth } from '../auth/index.js';
import { useMetrics } from '../hooks/use-metrics.js';
import { useStartServer, useStopServer } from '../hooks/use-server-actions.js';
import { useNotifications } from '../hooks/use-notifications.js';
import { StatusBadge } from './status-badge.js';
import { Button } from './button.js';
import { MetricBar } from './metric-bar.js';
import { ConfirmDialog } from './confirm-dialog.js';
import { ServerMenu } from './server-menu.js';

function formatUptime(launchTime: string): string {
  const diff = Date.now() - new Date(launchTime).getTime();
  if (diff < 0) return '—';
  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m`;
}

export const ServerCard = memo(function ServerCard({ server }: { server: Server }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: metrics } = useMetrics(server.id, server.status);
  const startServer = useStartServer();
  const stopServer = useStopServer();
  const { hasPermissionPending } = useNotifications();
  const [showStopDialog, setShowStopDialog] = useState(false);
  const permissionPending = hasPermissionPending(server.id);

  const isAdmin = user?.role === 'admin';
  const isTransitioning = server.status === 'starting' || server.status === 'stopping';
  const isRunning = server.status === 'running';
  const isStopped = server.status === 'stopped';

  function handleCardClick() {
    navigate(`/servers/${server.id}`);
  }

  function handleStart(e: React.MouseEvent) {
    e.stopPropagation();
    startServer.mutate(server.id);
  }

  function handleStopClick(e: React.MouseEvent) {
    e.stopPropagation();
    setShowStopDialog(true);
  }

  function handleStopConfirm() {
    setShowStopDialog(false);
    stopServer.mutate(server.id);
  }

  return (
    <>
      <div
        onClick={handleCardClick}
        onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        role="button"
        tabIndex={0}
        className="card cursor-pointer p-4 transition-all hover:shadow-md"
      >
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{server.organization}</h3>
            <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>{server.id} · {server.instanceType}</p>
          </div>
          <div className="flex items-center gap-2">
            {permissionPending && (
              <span className="badge badge-warn">Permission</span>
            )}
            <StatusBadge status={server.status} />
            {isAdmin && <ServerMenu server={server} />}
          </div>
        </div>

        {isRunning && server.lastStartedAt && (
          <p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
            Uptime: {formatUptime(server.lastStartedAt)}
            {server.activeSessions > 0 && ` · ${server.activeSessions} active session${server.activeSessions > 1 ? 's' : ''}`}
          </p>
        )}

        {isStopped && (
          <p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
            {server.stopReason ?? 'Unknown'}
            {server.stoppedBy && <span style={{ opacity: 0.6 }}>{` · ${server.stoppedBy}`}</span>}
            {server.stoppedAt && <span style={{ opacity: 0.6 }}>{` · ${new Date(server.stoppedAt).toLocaleString()}`}</span>}
          </p>
        )}

        {(isRunning || isTransitioning) && metrics && (
          <div className="mt-3 space-y-1.5">
            <MetricBar label="CPU" value={metrics.cpu.percent} />
            <MetricBar label="Mem" value={metrics.memory.percent} />
            <MetricBar label="Disk" value={metrics.disk.percent} />
          </div>
        )}

        {isAdmin && (
          <div className="mt-4 flex gap-2">
            {isStopped && (
              <Button
                onClick={handleStart}
                disabled={startServer.isPending}
                size="sm"
                style={{ backgroundColor: 'var(--color-success)' }}
              >
                {startServer.isPending && <Spinner />}
                Start
              </Button>
            )}
            {isRunning && (
              <Button
                variant="danger"
                onClick={handleStopClick}
                disabled={stopServer.isPending}
                size="sm"
              >
                {stopServer.isPending && <Spinner />}
                Stop
              </Button>
            )}
            {isTransitioning && (
              <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-text-3)' }}>
                <Spinner />
                {server.status === 'starting' ? 'Starting...' : 'Stopping...'}
              </span>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={showStopDialog}
        title="Stop server"
        message="This will stop the server and all active sessions. Continue?"
        confirmLabel="Stop server"
        onConfirm={handleStopConfirm}
        onCancel={() => setShowStopDialog(false)}
      />
    </>
  );
});

function Spinner() {
  return (
    <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
